import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMeQuery } from '@platform/state/api';
import { Spinner, Toasts, TooltipLayer } from '@platform/ui';
import { routes } from '@platform/core/lib/routes';
import { appRoutes } from './shell/appRoutes';
import { useServerPreferences } from '@platform/hooks/useServerPreferences';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { PageBreadcrumbs } from '@platform/navigation';
import { useAppDispatch } from '@platform/state/hooks';
import { workspaceSelected } from '@platform/state/slices/sessionSlice';
import { MiniFormHost } from '@platform/engine/form/mini/MiniFormHost';
import { ModuleMenu } from '@platform/engine/menu/ModuleMenu';
import { useAiBridge } from './shell/ai/useAiBridge';
import { AiPanel } from './shell/AiPanel';
import { ShellMenu } from './shell/ShellMenu';
import { ModuleNavigator } from './shell/navigator/ModuleNavigator';
import { useNavigatorOpen } from './shell/navigator/navigatorStore';
import { rememberModule } from './shell/navigator/recentModules';
import { landingWorkspace } from './shell/landingWorkspace';
import { useVisitedWorkspace } from './shell/useVisitedWorkspace';
import { useCustomizationScope } from './pages/system/useCustomizationScope';
import { TopBar } from './TopBar';
import './shell/layout.css';

/** Сторінки поза модулем, що рендерять власну смугу крихт: редактор форми й «Записи й форми» модуля в «Налаштуванні» (як на маршруті модуля). */
const OWN_CRUMBS = /^\/customize\/(forms|modules)\/[^/]+/;
/** Перелік модулів «Налаштування»: на рівні «Глобально» крихта розділу — «Каталог модулів» (прототип SYS_T['cust-mods']). */
const CATALOG = /^\/customize\/modules\/?$/;

/**
 * Оболонка робочого простору v4 (UIUX layout/app-shell): верхня панель 56px; під нею ліве меню 248px (меню модуля на всіх його маршрутах,
 * на системних сторінках — ShellMenu: вхідні, категорії «Усіх модулів» або меню Головної), колонка контенту на всю ширину — крихти системних сторінок (на сторінках модуля
 * й сторінках модуля в «Налаштуванні» їх рендерить сама сторінка) і основна область, що прокручується окремо; праворуч — панель ШІ-асистента (штовхає вміст; відкритість
 * і розмова відновлюються після перезавантаження, «Запитати ШІ» надсилає питання одразу — useAiBridge).
 * Поверх усього — навігатор модулів (Ctrl/⌘ K) і шар темних підказок (title → бульбашка через 1 с, controls/tooltip); відкриття модуля
 * запам'ятовується для його чипів «Нещодавні».
 * Застосовує серверні налаштування користувача (мова, схема). Робочий простір — зі стану сесії вкладки, не з адреси (ADR-028): без нього
 * (вхід, нова вкладка) чи без доступу до нього — простір входу (landingWorkspace: власний офіс депутата, інакше перший простір учасника),
 * без просторів — «Створити робочий простір». Простір, де користувач не учасник, відкривається, якщо сервер дає його читати (useVisitedWorkspace).
 */
export function WorkspaceLayout() {
  const { t } = useTranslation();
  const { ws, module, rt } = useWorkspaceParams();
  const { pathname } = useLocation();
  const global = useCustomizationScope(ws).scope === 'global';
  const dispatch = useAppDispatch();
  const { data: me, isLoading, isFetching, error } = useMeQuery();
  const aiOpen = useAiBridge(me?.id);
  const navigatorOpen = useNavigatorOpen();
  const visit = useVisitedWorkspace(ws, me);
  const landing = me ? landingWorkspace(me.workspaces) : undefined;
  // простору вкладки немає чи сервер його не дає читати (простір, щойно створений, поки «я» перечитується, — ще ні) → простір входу
  const lost = !ws || (visit === 'denied' && !isFetching);
  useEffect(() => { if (lost && landing && landing !== ws) dispatch(workspaceSelected(landing)); }, [lost, landing, ws, dispatch]);
  useEffect(() => { if (me && ws && module) rememberModule(me.id, ws, module); }, [me, ws, module]);
  useServerPreferences(me);
  if (isLoading) return <div className="login"><Spinner /></div>;
  if (error || !me) return <Navigate to={routes.login()} replace />;
  if (lost && !landing) return <Navigate to={appRoutes.newWorkspace()} replace />;
  if (lost || visit === 'loading' || visit === 'denied') return <div className="login"><Spinner /></div>;
  return (
    <div className="po-app">
      <TopBar ws={ws} />
      <div className="po-body">
        {module ? <ModuleMenu ws={ws} moduleKey={module} recordTypeKey={rt} /> : <ShellMenu ws={ws} pathname={pathname} />}
        <div className="ws-content">
          {!module && !OWN_CRUMBS.test(pathname) && <PageBreadcrumbs page={global && CATALOG.test(pathname) ? t('sysPages.sections.catalog') : undefined} />}
          <main className="shell__main ws-main"><Outlet /></main>
        </div>
        {aiOpen && <AiPanel ws={ws} />}
      </div>
      {navigatorOpen && <ModuleNavigator ws={ws} />}
      <MiniFormHost />
      <Toasts />
      <TooltipLayer />
    </div>
  );
}
